import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../components/ui/dialog';
import {
  CheckCircle, XCircle, Clock, CurrencyEur, User, FileText,
} from '@phosphor-icons/react';

const API = process.env.REACT_APP_BACKEND_URL;

const STATUS_STYLES = {
  pending:  { bg: 'bg-amber-50',  border: 'border-amber-200',  text: 'text-amber-700',  icon: Clock,       label: 'In attesa' },
  approved: { bg: 'bg-green-50',  border: 'border-green-200',  text: 'text-green-700',  icon: CheckCircle, label: 'Approvata' },
  rejected: { bg: 'bg-red-50',    border: 'border-red-200',    text: 'text-red-700',    icon: XCircle,     label: 'Rifiutata' },
};

export default function ApprovalsPage() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('pending');
  const [active, setActive] = useState(null); // dialog
  const [dialogMode, setDialogMode] = useState(null); // 'approve' | 'reject'
  const [importoApp, setImportoApp] = useState('');
  const [note, setNote] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const isReviewer = user?.role === 'admin' ||
    (user?.role === 'commerciale' && ['agente', 'canale_vendita'].includes(user?.sales_tier));

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const url = `${API}/api/extra-sconto/requests${statusFilter !== 'all' ? `?status=${statusFilter}` : ''}`;
      const res = await fetch(url, { credentials: 'include' });
      if (res.ok) setItems(await res.json());
      else toast.error('Errore caricamento richieste');
    } catch (e) { toast.error('Errore di rete'); }
    finally { setLoading(false); }
  }, [statusFilter]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const openApprove = (item) => {
    setActive(item);
    setDialogMode('approve');
    setImportoApp(String(item.importo_richiesto_eur_anno));
    setNote('');
  };
  const openReject = (item) => {
    setActive(item);
    setDialogMode('reject');
    setNote('');
  };

  const submitReview = async () => {
    if (!active || !dialogMode) return;
    if (dialogMode === 'reject' && note.trim().length < 3) {
      return toast.error('Motivazione del rifiuto obbligatoria');
    }
    setSubmitting(true);
    try {
      const body = dialogMode === 'approve'
        ? { importo_approvato_eur_anno: parseFloat(importoApp), note: note || null }
        : { note };
      const res = await fetch(`${API}/api/extra-sconto/requests/${active.id}/${dialogMode}`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok) {
        toast.success(dialogMode === 'approve' ? 'Richiesta approvata' : 'Richiesta rifiutata');
        setActive(null); setDialogMode(null);
        fetchData();
      } else {
        toast.error(data.detail || 'Errore');
      }
    } catch (e) { toast.error('Errore'); }
    finally { setSubmitting(false); }
  };

  return (
    <div className="space-y-6" data-testid="approvals-page">
      <div>
        <h1 className="text-3xl font-extrabold tracking-tight text-slate-900">Approvazioni Extra Sconto</h1>
        <p className="text-slate-600 mt-1">
          {isReviewer
            ? 'Richieste di extra sconto inoltrate dai sub-agenti che hai sotto di te (o, se admin, dell\'intera company).'
            : 'Le tue richieste di extra sconto inoltrate al tuo agente di riferimento.'}
        </p>
      </div>

      <div className="flex gap-2" data-testid="approvals-filter-bar">
        {[
          { v: 'pending', label: 'In attesa' },
          { v: 'approved', label: 'Approvate' },
          { v: 'rejected', label: 'Rifiutate' },
          { v: 'all', label: 'Tutte' },
        ].map(o => (
          <Button
            key={o.v}
            size="sm"
            variant={statusFilter === o.v ? 'default' : 'outline'}
            className={statusFilter === o.v ? 'bg-orange-500 hover:bg-orange-600' : ''}
            onClick={() => setStatusFilter(o.v)}
            data-testid={`filter-${o.v}-btn`}
          >
            {o.label}
          </Button>
        ))}
      </div>

      {loading ? (
        <div className="text-slate-500 text-sm">Caricamento…</div>
      ) : items.length === 0 ? (
        <div className="border border-dashed border-slate-300 rounded-lg p-12 text-center text-slate-500">
          Nessuna richiesta {statusFilter !== 'all' ? `(${statusFilter})` : ''} al momento.
        </div>
      ) : (
        <div className="grid gap-3">
          {items.map(it => {
            const st = STATUS_STYLES[it.status] || STATUS_STYLES.pending;
            const Icon = st.icon;
            const canReview = isReviewer && it.status === 'pending'
              && (user?.role === 'admin' || it.parent_user_id === user?.id);
            return (
              <div key={it.id} className={`border ${st.border} ${st.bg} rounded-lg p-4`} data-testid={`request-card-${it.id}`}>
                <div className="flex items-start justify-between flex-wrap gap-3">
                  <div className="flex-1 min-w-[280px]">
                    <div className="flex items-center gap-2 mb-2">
                      <Icon size={18} className={st.text} weight="bold" />
                      <span className={`font-semibold ${st.text}`}>{st.label}</span>
                      <Badge variant="outline" className="ml-2">{it.offer_name || '—'}</Badge>
                    </div>
                    <div className="text-sm text-slate-700 mb-1">
                      <FileText size={14} className="inline mr-1 text-slate-500" />
                      Cliente: <strong>{it.cliente_label || '—'}</strong>
                    </div>
                    <div className="text-sm text-slate-700 mb-1">
                      <User size={14} className="inline mr-1 text-slate-500" />
                      Richiedente: <strong>{it.requester_name}</strong> ({it.requester_email})
                    </div>
                    {it.motivo && (
                      <div className="text-sm text-slate-700 mt-2 italic border-l-2 border-slate-300 pl-3">
                        "{it.motivo}"
                      </div>
                    )}
                    {it.review_note && (
                      <div className="text-sm text-slate-600 mt-2 border-l-2 border-slate-400 pl-3">
                        <strong>Nota review:</strong> {it.review_note}
                      </div>
                    )}
                    <div className="text-xs text-slate-500 mt-2">
                      Inviata: {(it.created_at || '').slice(0, 16).replace('T', ' ')}
                      {it.reviewed_at && ` · Revisionata: ${it.reviewed_at.slice(0, 16).replace('T', ' ')}`}
                      {it.reviewer_email && ` da ${it.reviewer_email}`}
                    </div>
                  </div>

                  <div className="text-right">
                    <div className="text-xs text-slate-500">Richiesto</div>
                    <div className="text-2xl font-bold text-slate-900">
                      <CurrencyEur size={20} className="inline" weight="bold" />
                      {Number(it.importo_richiesto_eur_anno || 0).toFixed(2)}
                    </div>
                    <div className="text-xs text-slate-500">/anno</div>
                    {it.status === 'approved' && (
                      <div className="mt-2 text-green-700 text-sm font-semibold">
                        Approvato: €{Number(it.importo_approvato_eur_anno || 0).toFixed(2)}
                      </div>
                    )}
                  </div>
                </div>

                {canReview && (
                  <div className="flex gap-2 mt-3 pt-3 border-t border-slate-200">
                    <Button
                      size="sm"
                      className="bg-green-600 hover:bg-green-700"
                      onClick={() => openApprove(it)}
                      data-testid={`approve-btn-${it.id}`}
                    >
                      <CheckCircle size={14} className="mr-1" /> Approva
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      className="border-red-300 text-red-700 hover:bg-red-50"
                      onClick={() => openReject(it)}
                      data-testid={`reject-btn-${it.id}`}
                    >
                      <XCircle size={14} className="mr-1" /> Rifiuta
                    </Button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Approve / Reject dialog */}
      <Dialog open={!!active && !!dialogMode} onOpenChange={(o) => { if (!o) { setActive(null); setDialogMode(null); } }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>
              {dialogMode === 'approve' ? 'Approva richiesta extra sconto' : 'Rifiuta richiesta'}
            </DialogTitle>
          </DialogHeader>
          {active && (
            <div className="space-y-3">
              <div className="text-sm text-slate-700 bg-slate-50 p-3 rounded">
                <div>Richiedente: <strong>{active.requester_name}</strong></div>
                <div>Cliente: <strong>{active.cliente_label}</strong></div>
                <div>Offerta: <strong>{active.offer_name}</strong></div>
                <div>Richiesto: <strong>€{Number(active.importo_richiesto_eur_anno).toFixed(2)}/anno</strong></div>
                <div className="italic text-slate-600 mt-1">"{active.motivo}"</div>
              </div>
              {dialogMode === 'approve' && (
                <div>
                  <Label>Importo da approvare (€/anno)</Label>
                  <Input
                    type="number" step="0.01" min="0"
                    value={importoApp}
                    onChange={(e) => setImportoApp(e.target.value)}
                    data-testid="approve-amount-input"
                  />
                  <div className="text-xs text-slate-500 mt-1">
                    Puoi approvare un importo diverso da quello richiesto.
                  </div>
                </div>
              )}
              <div>
                <Label>Nota {dialogMode === 'reject' ? '(obbligatoria)' : '(opzionale)'}</Label>
                <Textarea
                  rows={3}
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  placeholder={dialogMode === 'reject' ? 'Motivazione del rifiuto…' : 'Note interne (opzionali)…'}
                  data-testid="review-note-input"
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="ghost" onClick={() => { setActive(null); setDialogMode(null); }} disabled={submitting}>
              Annulla
            </Button>
            <Button
              onClick={submitReview}
              disabled={submitting}
              className={dialogMode === 'approve' ? 'bg-green-600 hover:bg-green-700' : 'bg-red-600 hover:bg-red-700'}
              data-testid="submit-review-btn"
            >
              {submitting ? 'Invio…' : (dialogMode === 'approve' ? 'Conferma approvazione' : 'Conferma rifiuto')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
